import { TextInput } from "@carbon6/c6-frontend-design";
import { useEffect, useState, type ChangeEvent, type KeyboardEvent, type ReactNode } from "react";
import { ANNOTATION_SET_NAME_MAX_LENGTH } from "@/api";
import { HatTextModal } from "@/components/modal";

export type AnnotationSetNameModalProps = {
  open: boolean;
  label: string;
  /** 제목만으로 부족할 때만 넣는다. 없으면 모달은 제목, 입력, 버튼만 가진다. */
  caption?: ReactNode;
  okText: string;
  busy: boolean;
  /** 이름을 고치는 경우의 현재 값. 새로 만들 때는 비운다. */
  defaultName?: string;
  onSubmit: (name: string | null) => void;
  onCancel: () => void;
};

/**
 * Annotation Set 이름을 받는 모달. 만들 때와 이름을 고칠 때 함께 쓴다.
 *
 * 만들 때 이름을 묻는 이유: 이름을 나중에 채우게 하면 "(이름 없음)" 세트가 목록에 쌓여
 * 서로 구분되지 않는다. 그래서 이름 칸을 패널에 계속 두지 않고 이 모달에 모은다. 이름은
 * 비워 둘 수 있다. 비우면 서버에 `null` 을 보낸다.
 */
export function AnnotationSetNameModal({
  open,
  label,
  caption,
  okText,
  busy,
  defaultName = "",
  onSubmit,
  onCancel,
}: AnnotationSetNameModalProps) {
  const [name, setName] = useState(defaultName);

  // 열릴 때마다 현재 값에서 다시 시작한다. 닫힌 동안 남은 편집이 다음 열기에 남지 않는다.
  useEffect(() => {
    if (open) setName(defaultName);
  }, [defaultName, open]);

  const submit = () => {
    if (busy) return;
    onSubmit(name.trim() === "" ? null : name.trim());
  };

  return (
    <HatTextModal
      open={open}
      label={label}
      caption={caption}
      okText={okText}
      busy={busy}
      content={
        <TextInput
          value={name}
          aria-label="Annotation Set 이름"
          placeholder="이름 없음"
          maxLength={ANNOTATION_SET_NAME_MAX_LENGTH}
          disabled={busy}
          fullWidth
          onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            submit();
          }}
        />
      }
      onOkPress={submit}
      onCancelPress={onCancel}
    />
  );
}
